import React, { useState, useEffect, useMemo } from 'react';
import { ChemicalProduct } from '../../../types';
import { annotateUsageWithMetric, calculateDosage, formatDosageAmount } from '../../../utils/dosage';

interface DosageCalculatorProps {
  products: ChemicalProduct[];
  poolVolume: number;
  changeNeeded: number;
  label: string;
}

export const DosageCalculator: React.FC<DosageCalculatorProps> = ({
  products, poolVolume, changeNeeded, label,
}) => {
  const [selectedId, setSelectedId] = useState(products[0]?.id ?? '');

  useEffect(() => {
    const first = products[0];
    if (first !== undefined && !products.find(p => p.id === selectedId)) {
      setSelectedId(first.id);
    }
  }, [products, selectedId]);

  const product = products.find(p => p.id === selectedId);

  const dosage = useMemo(() => {
    if (!product || changeNeeded <= 0) return null;
    return calculateDosage(product, changeNeeded, poolVolume);
  }, [product, changeNeeded, poolVolume]);

  if (!products.length) return null;

  return (
    <div className="mt-2 pt-2 border-t border-slate-700">
      <p className="text-xs font-semibold text-cyan-400 mb-1">
        {label} ({changeNeeded.toFixed(2)} change needed):
      </p>
      <select
        value={selectedId}
        onChange={e => setSelectedId(e.target.value)}
        className="w-full p-1.5 bg-slate-900 border border-slate-600 rounded text-sm text-slate-200 mb-1"
      >
        {products.map(p => (
          <option key={p.id} value={p.id}>{p.brand} — {p.productName}</option>
        ))}
      </select>
      {dosage ? (
        <p className="text-sm font-bold text-cyan-300">
          → Add {formatDosageAmount(dosage.amount, dosage.unit)}
        </p>
      ) : (
        <p className="text-xs text-slate-500">
          Cannot calculate dosage for this product with the provided formula.
        </p>
      )}
      {product && (
        <p className="text-xs text-slate-500 mt-0.5">{annotateUsageWithMetric(product.usage)}</p>
      )}
    </div>
  );
};
